import { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  KEYS_OF_DRAFT_DATA,
  KEYS_OF_TEMP_DRAFT_DATA,
} from '../Component/Flow/constant'

const DEBOUNCE_MS = 500

/**
 * Custom hook to manage survey draft auto-save and recovery.
 *
 * Features:
 * - Auto-saves survey data to localStorage with debounce (500ms)
 * - Provides save status for UI indicator
 * - Supports draft recovery via getDraftData / saveDraftToTemp / clearDraft
 */
export const useSurveyDraft = () => {
  const savedSurveyFlow = useSelector(state => state.surveyFlow.ui.surveyFlow)
  const [saveStatus, setSaveStatus] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
  const debounceTimer = useRef(null)
  const statusTimer = useRef(null)

  /**
   * Auto-save current survey state to localStorage with debounce.
   */
  useEffect(() => {
    if (!savedSurveyFlow || !savedSurveyFlow.id) return

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    setSaveStatus('saving')

    debounceTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(KEYS_OF_DRAFT_DATA, JSON.stringify(savedSurveyFlow))
        setSaveStatus('saved')

        if (statusTimer.current) clearTimeout(statusTimer.current)
        statusTimer.current = setTimeout(() => setSaveStatus('idle'), 3000)
      } catch (err) {
        console.error('Failed to save draft:', err)
        setSaveStatus('error')
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [savedSurveyFlow])

  /**
   * Get the current draft data from localStorage.
   */
  const getDraftData = useCallback(() => {
    try {
      const draftData = localStorage.getItem(KEYS_OF_DRAFT_DATA)
      return draftData ? JSON.parse(draftData) : null
    } catch {
      return null
    }
  }, [])

  /**
   * Save current draft to temp storage (used before navigating away).
   */
  const saveDraftToTemp = useCallback(() => {
    try {
      const draftData = localStorage.getItem(KEYS_OF_DRAFT_DATA)
      if (draftData) {
        localStorage.setItem(KEYS_OF_TEMP_DRAFT_DATA, draftData)
        return JSON.parse(draftData)
      }
    } catch {
      // Ignore parse errors
    }
    return null
  }, [])

  /**
   * Clear all draft data from localStorage.
   */
  const clearDraft = useCallback(() => {
    localStorage.removeItem(KEYS_OF_DRAFT_DATA)
    localStorage.removeItem(KEYS_OF_TEMP_DRAFT_DATA)
    setSaveStatus('idle')
  }, [])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      if (statusTimer.current) clearTimeout(statusTimer.current)
    }
  }, [])

  return {
    saveStatus,
    getDraftData,
    saveDraftToTemp,
    clearDraft,
  }
}
